import { useLingui } from "@lingui/react/macro";
import type { Bot, ThreadMessage } from "@rakazo/contracts";
import { TICKET_DESCRIPTION_MAX_LENGTH } from "@rakazo/contracts";
import {
  messageAskBots,
  messageQuoteDraft,
  messageTaskAssignee,
  messageTaskTitle,
  truncatePlainText,
} from "@rakazo/core";
import { toast } from "@rakazo/ui-web";
import { useMemo } from "react";
import { readComposerDraft, writeComposerDraft } from "../../lib/composer-draft";
import { copyableMessageText } from "../../lib/message-text";
import type { createSpaceRpc } from "../../lib/rpc";
import type { MessageAskBot, MessageTeamActions } from "./message-hover-actions";

/**
 * Make a task files the message on the Space board; Ask opens another bot's chat with the
 * message quoted in its draft, for the owner to finish and send.
 */
export function useMessageTeamActions({
  rpc,
  bots,
  chatBotId,
  inGroup,
  boardEnabled,
  openBot,
}: {
  rpc: Pick<ReturnType<typeof createSpaceRpc>, "tickets">;
  bots: readonly Bot[];
  /** The bot whose one-to-one chat is open; undefined in a group. */
  chatBotId: string | undefined;
  inGroup: boolean;
  boardEnabled: boolean;
  openBot: (botId: string) => void;
}): MessageTeamActions {
  const { t } = useLingui();
  return useMemo(
    () => ({
      taskAssignee: (message: ThreadMessage) => {
        if (!boardEnabled || !messageTaskTitle(copyableMessageText(message))) return undefined;
        return messageTaskAssignee(message, inGroup ? undefined : chatBotId, bots);
      },
      makeTask: (message: ThreadMessage, assigneeBotId: string) => {
        const text = copyableMessageText(message);
        void rpc.tickets
          .create({
            title: messageTaskTitle(text),
            description: truncatePlainText(text, TICKET_DESCRIPTION_MAX_LENGTH),
            assigneeBotId,
          })
          .then(
            () => toast.success(t`Task created`),
            () => toast.error(t`Could not create task`),
          );
      },
      // Everyone in a group already reads the message; Reply with an @mention asks one of them
      // in place, so a private copy in another chat would only split the conversation.
      askBots: (message: ThreadMessage) =>
        inGroup || !copyableMessageText(message)
          ? []
          : messageAskBots(bots, [chatBotId, message.botId]),
      ask: (message: ThreadMessage, bot: MessageAskBot) => {
        const draftKey = `bot:${bot.id}`;
        writeComposerDraft(
          draftKey,
          messageQuoteDraft(copyableMessageText(message), readComposerDraft(draftKey)),
        );
        openBot(bot.id);
      },
    }),
    [boardEnabled, bots, chatBotId, inGroup, openBot, rpc, t],
  );
}
